const EXPORT_QUALITY = 0.92;

const lossyQuality = (type) =>
  type === "image/jpeg" || type === "image/webp" ? EXPORT_QUALITY : undefined;

const configureSmoothing = (ctx) => {
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
};

// Halve repeatedly, then do one final draw. One drawImage from a
// multi-megapixel photo uses low-quality filtering and looks soft.
const scaleSource = (source, targetWidth, targetHeight) => {
  let current = source;
  let width = source.naturalWidth || source.width;
  let height = source.naturalHeight || source.height;

  while (width * 0.5 > targetWidth && height * 0.5 > targetHeight) {
    width = Math.round(width * 0.5);
    height = Math.round(height * 0.5);

    const step = document.createElement("canvas");
    step.width = width;
    step.height = height;
    const stepCtx = step.getContext("2d");
    configureSmoothing(stepCtx);
    stepCtx.drawImage(current, 0, 0, width, height);
    current = step;
  }

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext("2d");
  configureSmoothing(ctx);
  ctx.drawImage(current, 0, 0, targetWidth, targetHeight);
  return canvas;
};

const canvasToFile = (canvas, file) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Image resizing failed"));
          return;
        }

        resolve(
          new File([blob], file.name, {
            type: blob.type || file.type,
            lastModified: Date.now(),
          })
        );
      },
      file.type,
      lossyQuality(file.type)
    );
  });

export const resizeFile = (file, maxWidth, maxHeight) =>
  new Promise((resolve, reject) => {
    if (!file?.type?.startsWith("image/")) {
      resolve(file);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = async () => {
      URL.revokeObjectURL(objectUrl);

      try {
        const sourceWidth = img.naturalWidth || img.width;
        const sourceHeight = img.naturalHeight || img.height;

        if (sourceWidth <= maxWidth && sourceHeight <= maxHeight) {
          resolve(file);
          return;
        }

        const ratio = Math.min(maxWidth / sourceWidth, maxHeight / sourceHeight);
        const targetWidth = Math.max(1, Math.round(sourceWidth * ratio));
        const targetHeight = Math.max(1, Math.round(sourceHeight * ratio));
        const canvas = scaleSource(img, targetWidth, targetHeight);

        resolve(await canvasToFile(canvas, file));
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Invalid image file"));
    };

    img.src = objectUrl;
  });
